import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Alert } from '../components/auth/Alert.js';
import { RecoveryCodeInput } from '../components/auth/RecoveryCodeInput.js';
import { AuthLayout } from '../layouts/AuthLayout.js';
import { AUTH_STORAGE_KEYS, recoveryCodeVerifyRequest } from '../lib/auth-api.js';

type AlertState =
  | { kind: 'invalid_code' }
  | { kind: 'rate_limited'; retryAfterSeconds: number | null }
  | { kind: 'network_error'; message: string }
  | null;

// A complete recovery code is XXXX-XXXX-XXXX-XXXX = 19 characters.
// RecoveryCodeInput auto-formats, so the post-format value is the
// canonical form and we can length-check it directly.
const COMPLETE_LENGTH = 19;

export function RecoveryCodePage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<AlertState>(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem(AUTH_STORAGE_KEYS.challengeToken);
    if (!token) navigate('/login');
  }, [navigate]);

  const canSubmit = !submitting && code.length === COMPLETE_LENGTH;

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    const token = sessionStorage.getItem(AUTH_STORAGE_KEYS.challengeToken);
    if (!token) {
      navigate('/login');
      return;
    }
    setSubmitting(true);
    setAlert(null);
    const result = await recoveryCodeVerifyRequest(token, code);
    if (result.ok) {
      sessionStorage.removeItem(AUTH_STORAGE_KEYS.challengeToken);
      navigate('/');
      return;
    }
    setSubmitting(false);
    if (result.kind === 'challenge_expired') {
      setExpired(true);
      return;
    }
    if (result.kind === 'invalid_code') {
      setAlert({ kind: 'invalid_code' });
      return;
    }
    if (result.kind === 'rate_limited') {
      setAlert({ kind: 'rate_limited', retryAfterSeconds: result.retryAfterSeconds });
      return;
    }
    setAlert({ kind: 'network_error', message: result.message });
  }

  if (expired) {
    return (
      <AuthLayout title="Session de connexion expirée">
        <Alert variant="error">
          Votre session de connexion a expiré. Reconnectez-vous pour utiliser un code de
          récupération.
        </Alert>
        <Link to="/login" className="btn-primary w-full mt-6" data-testid="recovery-back-to-login">
          Retour à la connexion
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Code de récupération"
      subtitle="Saisissez l’un de vos codes de récupération à usage unique."
      belowCard={
        <p className="text-sm text-center">
          <Link to="/mfa-verify" className="btn-ghost" data-testid="recovery-back-to-mfa">
            Retour à la vérification par code à 6 chiffres
          </Link>
        </p>
      }
    >
      <div className="mb-4">
        <Alert variant="warning" title="Un email d’alerte de sécurité va être envoyé">
          Si vous utilisez un code de récupération, vous recevrez un email d’alerte de sécurité.
          Pensez à régénérer vos codes depuis la page Sécurité après votre connexion.
        </Alert>
      </div>

      {alert ? (
        <div className="mb-4">
          <AlertView alert={alert} />
        </div>
      ) : null}

      <form onSubmit={onSubmit}>
        <RecoveryCodeInput
          label="Code de récupération"
          value={code}
          onChange={(next) => {
            setCode(next);
            // Clear inline error as soon as the user starts typing again.
            if (alert) setAlert(null);
          }}
          disabled={submitting}
        />

        <button
          type="submit"
          disabled={!canSubmit}
          aria-busy={submitting || undefined}
          className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="recovery-submit"
        >
          {submitting ? 'Vérification…' : 'Vérifier'}
        </button>
      </form>
    </AuthLayout>
  );
}

function AlertView({ alert }: { alert: NonNullable<AlertState> }) {
  if (alert.kind === 'invalid_code') {
    return (
      <Alert variant="error">
        Code invalide. Vérifiez la saisie ou utilisez un autre code de récupération.
      </Alert>
    );
  }
  if (alert.kind === 'rate_limited') {
    const sec = alert.retryAfterSeconds;
    const body =
      sec === null
        ? 'Trop de tentatives. Patientez un instant avant de réessayer.'
        : `Trop de tentatives. Patientez environ ${Math.max(1, sec)} seconde${
            sec > 1 ? 's' : ''
          } avant de réessayer.`;
    return (
      <Alert variant="warning" title="Trop de tentatives">
        {body}
      </Alert>
    );
  }
  return <Alert variant="error">{alert.message}</Alert>;
}
